/**
 * Work Requests Service - API calls for work requests management
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be';

/**
 * Request types with colors
 */
export const REQUEST_TYPES = {
    job_fulltime: { label: 'Plein-temps', color: '#3b82f6' },      // blue
    job_parttime: { label: 'Mi-temps', color: '#60a5fa' },          // blue-light
    job_hours: { label: 'Quelques heures', color: '#06b6d4' },      // cyan
    home_service: { label: 'Service à domicile', color: '#22c55e' }, // green
    project: { label: 'Pour un projet', color: '#8b5cf6' },          // violet
    family: { label: 'Pour la famille', color: '#f97316' },          // orange
    friend: { label: 'Pour un ami', color: '#ec4899' },              // pink
};

/**
 * Urgency badges with colors and sort order
 */
export const URGENCY_BADGES = {
    urgent: { label: 'Urgent', color: '#ef4444', order: 1 },         // red
    this_week: { label: 'Cette semaine', color: '#f97316', order: 2 }, // orange
    asap: { label: 'Dès que possible', color: '#8b5cf6', order: 3 },  // violet
    this_month: { label: 'Ce mois-ci', color: '#eab308', order: 4 },  // yellow
    three_months: { label: 'Dans 3 mois', color: '#84cc16', order: 5 }, // lime
    six_months: { label: 'Dans 6 mois', color: '#06b6d4', order: 6 },  // cyan
    this_year: { label: 'Dans l\'année', color: '#6b7280', order: 7 }, // gray
};

/**
 * Compensation types with colors
 */
export const COMPENSATION_TYPES = {
    paid: { label: 'Rémunéré', color: '#22c55e' },        // green
    exchange: { label: 'Échange', color: '#f59e0b' },     // amber
    volunteer: { label: 'Bénévolat', color: '#ec4899' },  // pink
};

/**
 * Duration types with colors
 */
export const DURATION_TYPES = {
    hour: { label: 'Quelques heures', color: '#60a5fa' },    // blue-light
    days: { label: 'Quelques jours', color: '#3b82f6' },     // blue
    months: { label: 'Quelques mois', color: '#8b5cf6' },    // violet
    year: { label: 'Un an ou plus', color: '#6366f1' },      // indigo
    undefined: { label: 'Indéterminé', color: '#6b7280' },   // gray
};

/**
 * Get all public work requests
 * @param {Object} filters - Filters (type, urgency, search, sortBy, sortDir)
 * @returns {Promise<Array>} List of requests
 */
export const getPublicRequests = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.type) params.append('type', filters.type);
        if (filters.urgency) params.append('urgency', filters.urgency);
        if (filters.search) params.append('search', filters.search);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDir) params.append('sortDir', filters.sortDir);

        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests?${params}`);
        if (!response.ok) throw new Error('Failed to fetch requests');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const requests = data.data ?? data.DATA ?? [];
        return success ? requests : [];
    } catch (error) {
        console.error('Error fetching public requests:', error);
        return [];
    }
};

/**
 * Get user's own requests
 * @param {number} userId - User ID
 * @returns {Promise<Array>} List of user's requests
 */
export const getMyRequests = async (userId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/my?userId=${userId}`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch my requests');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const requests = data.data ?? data.DATA ?? [];
        return success ? requests : [];
    } catch (error) {
        console.error('Error fetching my requests:', error);
        return [];
    }
};

/**
 * Get single request by ID
 * @param {number} requestId - Request ID
 * @returns {Promise<Object|null>} Request data or null
 */
export const getRequestById = async (requestId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}`);
        if (!response.ok) throw new Error('Failed to fetch request');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const request = data.data ?? data.DATA ?? null;
        return success ? request : null;
    } catch (error) {
        console.error('Error fetching request:', error);
        return null;
    }
};

/**
 * Create a new work request
 * @param {Object} requestData - Request data
 * @returns {Promise<Object>} Result with success status
 */
export const createRequest = async (requestData) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestData)
        });
        const result = await response.json();
        // Normalize ColdFusion uppercase keys
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE,
            requestId: result.requestId ?? result.REQUESTID
        };
    } catch (error) {
        console.error('Error creating request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a work request
 * @param {number} requestId - Request ID
 * @param {Object} requestData - Update data
 * @returns {Promise<Object>} Result with success status
 */
export const updateRequest = async (requestId, requestData) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestData)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a work request
 * @param {number} requestId - Request ID
 * @param {number} userId - User ID (for ownership verification)
 * @returns {Promise<Object>} Result with success status
 */
export const deleteRequest = async (requestId, userId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}?userId=${userId}`, {
            method: 'DELETE'
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Close a work request
 * @param {number} requestId - Request ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Result with success status
 */
export const closeRequest = async (requestId, userId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/close`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error closing request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get type color
 * @param {string} type - Request type
 * @returns {string} Color hex
 */
export const getTypeColor = (type) => REQUEST_TYPES[type]?.color || '#6b7280';

/**
 * Get type label
 * @param {string} type - Request type
 * @returns {string} Label
 */
export const getTypeLabel = (type) => REQUEST_TYPES[type]?.label || type;

/**
 * Get urgency color
 * @param {string} urgency - Urgency badge
 * @returns {string} Color hex
 */
export const getUrgencyColor = (urgency) => URGENCY_BADGES[urgency]?.color || '#6b7280';

/**
 * Get urgency label
 * @param {string} urgency - Urgency badge
 * @returns {string} Label
 */
export const getUrgencyLabel = (urgency) => URGENCY_BADGES[urgency]?.label || urgency;

/**
 * Get compensation color
 * @param {string} compensation - Compensation type
 * @returns {string} Color hex
 */
export const getCompensationColor = (compensation) => COMPENSATION_TYPES[compensation]?.color || '#6b7280';

/**
 * Get compensation label
 * @param {string} compensation - Compensation type
 * @returns {string} Label
 */
export const getCompensationLabel = (compensation) => COMPENSATION_TYPES[compensation]?.label || compensation;

/**
 * Get duration color
 * @param {string} duration - Duration type
 * @returns {string} Color hex
 */
export const getDurationColor = (duration) => DURATION_TYPES[duration]?.color || '#6b7280';

/**
 * Get duration label
 * @param {string} duration - Duration type
 * @returns {string} Label
 */
export const getDurationLabel = (duration) => DURATION_TYPES[duration]?.label || duration;

/**
 * Get occupations for a work request
 * @param {number} requestId - Request ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of occupations
 */
export const getRequestOccupations = async (requestId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/occupations?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch occupations');
        const data = await response.json();
        return data.success ? data.data : [];
    } catch (error) {
        console.error('Error fetching request occupations:', error);
        return [];
    }
};

/**
 * Get skills for a work request
 * @param {number} requestId - Request ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of skills
 */
export const getRequestSkills = async (requestId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/skills?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch skills');
        const data = await response.json();
        return data.success ? data.data : [];
    } catch (error) {
        console.error('Error fetching request skills:', error);
        return [];
    }
};

/**
 * Get creator profile with photo by peopleUuid
 * @param {string} peopleUuid - Creator's UUID
 * @returns {Promise<Object>} Profile data with photo
 */
export const getCreatorProfile = async (peopleUuid) => {
    try {
        // Get profile info
        const profileResponse = await fetch(`${API_BASE}/rest/cabaza/peoples/${peopleUuid}`);
        if (!profileResponse.ok) throw new Error('Failed to fetch profile');
        const profileData = await profileResponse.json();

        // Get profile photo
        let photoUrl = null;
        try {
            const photoResponse = await fetch(`${API_BASE}/rest/cabaza/files/profile-photo/${peopleUuid}`);
            if (photoResponse.ok) {
                const photoData = await photoResponse.json();
                if (photoData.success && photoData.files && photoData.files.length > 0) {
                    photoUrl = photoData.files[0].publicurl || photoData.files[0].publicUrl;
                }
            }
        } catch (photoError) {
            console.log('No profile photo found');
        }

        return {
            success: true,
            profile: profileData.success ? profileData.data : null,
            photoUrl
        };
    } catch (error) {
        console.error('Error fetching creator profile:', error);
        return { success: false, profile: null, photoUrl: null };
    }
};
